import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Linkedin, Globe, ArrowRight } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import { TEAM } from '../data/team';
import { isEnglishPath } from '../utils/i18nRoutes';

const DAVID = TEAM.find((m) => m.slug === 'david-redondo-perez');

const ALTERNATES = [
  { hreflang: 'es', href: '/equipo/david-redondo-perez' },
  { hreflang: 'en', href: '/en/team/david-redondo-perez' },
  { hreflang: 'x-default', href: '/equipo/david-redondo-perez' },
];

const CONTENT = {
  es: {
    seoTitle: 'David Redondo Pérez | Head of Innovation & AI | Valoriza',
    seoDescription: 'Head of Innovation & AI en Valoriza. Operador de IA en servicios urbanos: TrisTrash, Pando y un sistema de innovación repetible.',
    breadcrumb: [{ label: 'Inicio', to: '/' }, { label: 'Equipo', to: '/equipo' }, { label: 'David Redondo Pérez' }],
    leadLabel: 'Lead',
    lead: 'Ingeniero agrónomo. Head of Innovation & AI en Valoriza Servicios Medioambientales. Más de diez años haciendo que la tecnología sobreviva al turno, no solo a la demo.',
    tesisLabel: 'Tesis',
    tesis: 'Valoriza no necesita más PowerPoints de “smart city”. Necesita IA que opere: en la flota, en la planta, en la gente. David construye el sistema para que eso se repita — no un piloto huérfano.',
    bioLabel: 'Bio',
    bio1: 'Lidera la función de innovación de Valoriza: IA aplicada a servicios urbanos, I+D+i y un proceso que el Comité puede gobernar.',
    bio2: 'Antes de este cargo: arquitectura de datos, GIS y producto digital en operación. El hilo es el mismo — que la decisión de un operario a las 06:00 tenga mejor información que la de un comité a las 12:00.',
    bio3: 'Tres pruebas en público:',
    proofs: [
      { strong: 'TrisTrash', text: ' — visión artificial embarcada; la calle como sensor.' },
      { strong: 'Pando', text: ' — copiloto de IA para +500 personas, con gobierno y AI Act.' },
      { strong: 'El propio sistema de innovación', text: ' — retos 2026, vehículos, ISO 56001 en curso, conversación pública (Garrigues, South Summit).' },
    ],
    linksLabel: 'Enlaces',
    linkTristrash: 'TrisTrash',
    linkPando: 'Pando',
    linkTeam: 'Todo el equipo',
    ctaText: 'Hablar con el equipo',
    tristrashPath: '/tristrash',
    pandoPath: '/pando',
    teamPath: '/equipo',
    contactPath: '/#contacto',
  },
  en: {
    seoTitle: 'David Redondo Pérez | Head of Innovation & AI | Valoriza',
    seoDescription: 'Head of Innovation & AI at Valoriza. Running AI in urban services: TrisTrash, Pando and a repeatable innovation system.',
    breadcrumb: [{ label: 'Home', to: '/en' }, { label: 'Team', to: '/en/team' }, { label: 'David Redondo Pérez' }],
    leadLabel: 'Lead',
    lead: 'Agronomist engineer. Head of Innovation & AI at Valoriza Servicios Medioambientales. Over ten years making technology survive the shift, not just the demo.',
    tesisLabel: 'Thesis',
    tesis: 'Valoriza doesn’t need more “smart city” slide decks. It needs AI that operates: on the fleet, on the plant floor, with the people. David builds the system so that repeats — not an orphaned pilot.',
    bioLabel: 'Bio',
    bio1: 'Leads Valoriza’s innovation function: AI applied to urban services, R&D and a process the Committee can govern.',
    bio2: 'Before this role: data architecture, GIS and digital product in operations. The thread is the same — that an operator’s decision at 6am has better information than a committee’s at noon.',
    bio3: 'Three proofs in public:',
    proofs: [
      { strong: 'TrisTrash', text: ' — onboard computer vision; the street as a sensor.' },
      { strong: 'Pando', text: ' — AI copilot for +500 people, with governance and AI Act compliance.' },
      { strong: 'The innovation system itself', text: ' — 2026 challenges, execution vehicles, ISO 56001 underway, public conversation (Garrigues, South Summit).' },
    ],
    linksLabel: 'Links',
    linkTristrash: 'TrisTrash',
    linkPando: 'Pando',
    linkTeam: 'Full team',
    ctaText: 'Talk to the team',
    tristrashPath: '/en/tristrash',
    pandoPath: '/en/pando',
    teamPath: '/en/team',
    contactPath: '/en#contacto',
  },
};

export default function EquipoDavidPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/team/david-redondo-perez' : '/equipo/david-redondo-perez';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': 'https://innovation.valorizasm.com/equipo/david-redondo-perez#person',
    name: 'David Redondo Pérez',
    jobTitle: 'Head of Innovation & AI',
    url: 'https://innovation.valorizasm.com/equipo/david-redondo-perez',
    worksFor: { '@id': 'https://innovation.valorizasm.com/#organization' },
    sameAs: [
      'https://www.linkedin.com/in/davidredondoperez',
      'https://davidredondo.xyz',
    ],
  };

  return (
    <>
      <SEOHead
        title={c.seoTitle}
        description={c.seoDescription}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        ogType="profile"
        jsonLd={[jsonLd]}
      />
      <article className="pt-32 pb-24 v-container" data-testid="david-page">
        <Breadcrumb items={c.breadcrumb} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-12">
          <div className="w-28 h-28 rounded-full flex-shrink-0 overflow-hidden" style={{ background: `${DAVID.color}20`, border: `3px solid ${DAVID.color}40` }}>
            <img src={DAVID.photo} alt="David Redondo Pérez, Head of Innovation & AI" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-black dark:text-white/95 mb-1 tracking-tight" data-testid="david-h1">David Redondo Pérez</h1>
            <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: DAVID.color }}>Head of Innovation & AI</p>
            <div className="flex items-center gap-4 flex-wrap">
              <a href="https://www.linkedin.com/in/davidredondoperez" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-white/60 hover:text-[#8DC21F] transition-colors" data-testid="david-linkedin-link">
                <Linkedin size={15} /> LinkedIn
              </a>
              <a href="https://davidredondo.xyz" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-white/60 hover:text-[#8DC21F] transition-colors" data-testid="david-website-link">
                <Globe size={15} /> davidredondo.xyz
              </a>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{c.leadLabel}</h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{c.lead}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{c.tesisLabel}</h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">{c.tesis}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-black dark:text-white/90 mb-3">{c.bioLabel}</h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed mb-4">{c.bio1}</p>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed mb-4">{c.bio2}</p>
              <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed mb-3">{c.bio3}</p>
              <ol className="space-y-2">
                {c.proofs.map((p, i) => (
                  <li key={i} className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed"><strong className="text-black dark:text-white">{p.strong}</strong>{p.text}</li>
                ))}
              </ol>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{c.linksLabel}</h3>
              <div className="flex flex-col gap-2.5 text-sm">
                <Link to={c.tristrashPath} className="hover:text-[#8DC21F] transition-colors">{c.linkTristrash}</Link>
                <Link to={c.pandoPath} className="hover:text-[#8DC21F] transition-colors">{c.linkPando}</Link>
                <Link to={c.teamPath} className="hover:text-[#8DC21F] transition-colors">{c.linkTeam}</Link>
              </div>
            </div>
            <Link to={c.contactPath} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="david-cta-contact">
              {c.ctaText} <ArrowRight size={15} />
            </Link>
          </aside>
        </div>
      </article>
    </>
  );
}
